import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {getAdminUsersSearch} from '../../api';
import * as colors from '../../util/colors';
import Button from './Button';
import {ErrorAlert} from './Alerts';
import Modal, {Close} from './Modal';

const getErrorMessage = async (response, fallback) => {
  try {
    const data = await response.json();
    return data.error || fallback;
  } catch (e) {
    return fallback;
  }
};

const formatAdminUserDate = (value) => {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleDateString('ru-RU');
};

export const formatAdminUserAmount = (value) => {
  const amount = Number(value || 0) / 100;

  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatAdminUserName = (user) => {
  if (!user) {
    return 'Не выбран';
  }

  const fullName = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  return fullName || 'Без имени';
};

function AdminUserPicker({
  authorization,
  selectedUser,
  onSelect,
  label,
  emptyTitle,
  emptyDescription,
  buttonText,
  changeButtonText,
  searchTitle,
  searchDescription,
  roleFilter,
  excludeRoleFilter,
}) {
  const [searchVisible, setSearchVisible] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchError, setSearchError] = React.useState('');

  React.useEffect(() => {
    if (!searchVisible) {
      return undefined;
    }

    const normalizedSearch = searchValue.trim();

    if (normalizedSearch.length < 3) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchError('');
      return undefined;
    }

    let cancelled = false;
    const timeoutId = setTimeout(() => {
      setSearchLoading(true);
      setSearchError('');

      getAdminUsersSearch({authorization}, normalizedSearch, {
        role: roleFilter,
        excludeRole: excludeRoleFilter,
      })
        .then(async response => {
          if ((response.status >= 200) && (response.status < 300)) {
            return response.json();
          }

          throw new Error(await getErrorMessage(response, 'Не удалось найти пользователей'));
        })
        .then((users) => {
          if (!cancelled) {
            setSearchResults(Array.isArray(users) ? users : []);
          }
        })
        .catch((error) => {
          if (!cancelled) {
            setSearchResults([]);
            setSearchError(error.message || 'Не удалось найти пользователей');
          }
        })
        .finally(() => {
          if (!cancelled) {
            setSearchLoading(false);
          }
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [authorization, excludeRoleFilter, roleFilter, searchValue, searchVisible]);

  const openSearch = React.useCallback(() => {
    setSearchVisible(true);
    setSearchValue('');
    setSearchResults([]);
    setSearchLoading(false);
    setSearchError('');
  }, []);

  const closeSearch = React.useCallback(() => {
    setSearchVisible(false);
    setSearchValue('');
    setSearchResults([]);
    setSearchLoading(false);
    setSearchError('');
  }, []);

  const handleSelect = React.useCallback((user) => {
    onSelect(user);
    closeSearch();
  }, [closeSearch, onSelect]);

  const showSearchHint = searchValue.trim().length < 3;
  const selectedUserIsCompany = selectedUser && selectedUser.role === 'company';

  return (
    <>
      <FieldGroup>
        <FieldLabel>{label}</FieldLabel>
        <SelectorRow>
          <SelectedUserCard isEmpty={!selectedUser}>
            {!selectedUser && (
              <>
                <UserMainText>{emptyTitle}</UserMainText>
                <UserMetaText>{emptyDescription}</UserMetaText>
              </>
            )}

            {selectedUser && (
              <>
                <UserMainLine>
                  <UserMainText>{formatAdminUserName(selectedUser)}</UserMainText>
                  <BalanceBadge>{formatAdminUserAmount(selectedUser.account_amount)} ₽</BalanceBadge>
                </UserMainLine>
                {!selectedUserIsCompany && (
                  <UserMetaText>{selectedUser.phone || '—'}</UserMetaText>
                )}
                <UserMetaLine>
                  <span>{selectedUser.email || '—'}</span>
                  <span>Регистрация: {formatAdminUserDate(selectedUser.created_at)}</span>
                </UserMetaLine>
              </>
            )}
          </SelectedUserCard>

          <ActionButton
            theme="white-blue"
            width="220px"
            margin="0"
            onClick={openSearch}
            type="button"
          >
            {selectedUser ? changeButtonText : buttonText}
          </ActionButton>
        </SelectorRow>
      </FieldGroup>

      {searchVisible && (
        <Modal width="760px" minHeight="auto" maxHeight="85vh" padding="32px">
          <Close onClick={closeSearch}>
            <img src="/cross.svg" alt="Close" />
          </Close>

          <ModalContent>
            <ModalTitle>{searchTitle}</ModalTitle>
            <ModalDescription>{searchDescription}</ModalDescription>

            <FieldGroup>
              <FieldLabel htmlFor="admin-user-search">Поиск пользователя</FieldLabel>
              <FieldInput
                id="admin-user-search"
                type="text"
                value={searchValue}
                autoFocus
                placeholder="Введите телефон, имя, фамилию или email"
                onChange={(event) => {
                  setSearchValue(event.target.value);
                  setSearchError('');
                }}
              />
            </FieldGroup>

            {searchError && (
              <AlertWrap>
                <ErrorAlert noTriangle>{searchError}</ErrorAlert>
              </AlertWrap>
            )}

            {showSearchHint && (
              <StateBlock>Введите минимум 3 символа для поиска.</StateBlock>
            )}

            {!showSearchHint && searchLoading && (
              <StateBlock>Ищем пользователей...</StateBlock>
            )}

            {!showSearchHint && !searchLoading && !searchError && (searchResults.length === 0) && (
              <StateBlock>Ничего не найдено.</StateBlock>
            )}

            {!showSearchHint && !searchLoading && (searchResults.length > 0) && (
              <ResultsList>
                {searchResults.map(user => {
                  const isCompany = user.role === 'company';

                  return (
                    <ResultButton
                      key={user.id}
                      type="button"
                      onClick={() => handleSelect(user)}
                    >
                      <ResultHeader>
                        <ResultName>{formatAdminUserName(user)}</ResultName>
                        <BalanceBadge>{formatAdminUserAmount(user.account_amount)} ₽</BalanceBadge>
                      </ResultHeader>
                      {!isCompany && <ResultMeta>{user.phone || '—'}</ResultMeta>}
                      <ResultFooter>
                        <span>{user.email || '—'}</span>
                        <span>Регистрация: {formatAdminUserDate(user.created_at)}</span>
                      </ResultFooter>
                    </ResultButton>
                  );
                })}
              </ResultsList>
            )}
          </ModalContent>
        </Modal>
      )}
    </>
  );
}

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.label`
  font-size: 14px;
  color: ${colors.TEXT_MAIN};
  font-weight: bold;
`;

const SelectorRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 220px;
  gap: 16px;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SelectedUserCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 96px;
  padding: 16px;
  border: 1px solid ${props => props.isEmpty ? colors.INPUT_STROKE : '#D7E5FF'};
  border-radius: 12px;
  background: ${props => props.isEmpty ? 'white' : colors.BLUE_LIGHT};
`;

const UserMainLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const UserMainText = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: ${colors.TEXT_TITLE};
`;

const UserMetaText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: ${colors.TEXT_MAIN};
`;

const UserMetaLine = styled.div`
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  font-size: 13px;
  line-height: 1.5;
  color: ${colors.TEXT_MINOR};
`;

const BalanceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 4px 10px;
  border-radius: 999px;
  background: white;
  color: ${colors.BLUE_MAIN};
  font-size: 13px;
  font-weight: bold;
  white-space: nowrap;
`;

const ActionButton = styled(Button)`
  align-self: stretch;

  @media screen and (max-width: 768px) {
    min-width: 100%;
    max-width: 100%;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const ModalTitle = styled.h3`
  margin: 0;
  padding-right: 32px;
  font-size: 22px;
  line-height: 1.2;
  color: ${colors.TEXT_TITLE};
`;

const ModalDescription = styled.div`
  margin-top: -8px;
  font-size: 14px;
  line-height: 1.6;
  color: ${colors.TEXT_MINOR};
`;

const FieldInput = styled.input`
  width: 100%;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid ${colors.INPUT_STROKE};
  border-radius: 8px;
  outline: none;
  font-size: 15px;
  color: ${colors.TEXT_MAIN};
  background: white;

  &:focus {
    border-color: ${colors.BUTTON_BLUE};
    box-shadow: 0 0 0 3px rgba(71, 146, 255, 0.12);
  }
`;

const AlertWrap = styled.div`
  max-width: 100%;
`;

const StateBlock = styled.div`
  padding: 16px;
  border-radius: 10px;
  background: ${colors.BG};
  color: ${colors.TEXT_MINOR};
  font-size: 14px;
  line-height: 1.5;
`;

const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ResultButton = styled.button`
  width: 100%;
  padding: 16px;
  border: 1px solid ${colors.LINE};
  border-radius: 12px;
  background: white;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover,
  &:focus {
    border-color: ${colors.BUTTON_BLUE};
    box-shadow: 0 10px 24px rgba(46, 131, 255, 0.12);
    transform: translateY(-1px);
    outline: none;
  }
`;

const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const ResultName = styled.div`
  font-size: 16px;
  font-weight: bold;
  line-height: 1.4;
  color: ${colors.TEXT_TITLE};
`;

const ResultMeta = styled.div`
  margin-top: 6px;
  font-size: 14px;
  line-height: 1.5;
  color: ${colors.TEXT_MAIN};
`;

const ResultFooter = styled.div`
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.5;
  color: ${colors.TEXT_MINOR};
`;

AdminUserPicker.propTypes = {
  authorization: PropTypes.string,
  selectedUser: PropTypes.shape({
    id: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    created_at: PropTypes.string,
    account_amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    role: PropTypes.string,
    shared_account_id: PropTypes.string,
    shared_account: PropTypes.shape({
      id: PropTypes.string,
      email: PropTypes.string,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      account_amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  }),
  onSelect: PropTypes.func.isRequired,
  label: PropTypes.string,
  emptyTitle: PropTypes.string,
  emptyDescription: PropTypes.string,
  buttonText: PropTypes.string,
  changeButtonText: PropTypes.string,
  searchTitle: PropTypes.string,
  searchDescription: PropTypes.string,
  roleFilter: PropTypes.string,
  excludeRoleFilter: PropTypes.string,
};

AdminUserPicker.defaultProps = {
  authorization: '',
  selectedUser: null,
  label: 'Пользователь',
  emptyTitle: 'Пользователь не выбран',
  emptyDescription: 'Откройте поиск и выберите пользователя по телефону, имени, фамилии или email.',
  buttonText: 'Найти пользователя',
  changeButtonText: 'Сменить пользователя',
  searchTitle: 'Найти пользователя',
  searchDescription: 'Поиск начинается после 3 символов. Доступны телефон, имя, фамилия и email.',
  roleFilter: '',
  excludeRoleFilter: '',
};

export default AdminUserPicker;
